import { useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Ban } from "lucide-react";

export default function HiredEquipmentMaster() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [newName, setNewName] = useState("");
  const [newContractor, setNewContractor] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editContractor, setEditContractor] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    client
      .get("/diesel-register/hired-equipment", { params: { include_inactive: includeInactive } })
      .then((res) => setItems(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [includeInactive]);

  const addItem = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return toast.error("Equipment Name/Description is required");
    setBusy(true);
    try {
      await client.post("/diesel-register/hired-equipment", {
        equipment_name: newName.trim(),
        contractor_owner: newContractor.trim() || null,
      });
      toast.success("Hired Equipment added");
      setNewName("");
      setNewContractor("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const rename = async (code) => {
    if (!editName.trim()) return toast.error("Equipment Name/Description is required");
    setBusy(true);
    try {
      await client.put(`/diesel-register/hired-equipment/${encodeURIComponent(code)}`, {
        equipment_name: editName.trim(),
        contractor_owner: editContractor.trim() || null,
      });
      toast.success("Hired Equipment updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (code) => {
    setBusy(true);
    try {
      await client.delete(`/diesel-register/hired-equipment/${encodeURIComponent(code)}`);
      toast.success("Hired Equipment deactivated");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="hired-equipment-master">
      <form onSubmit={addItem} className="flex flex-wrap gap-2 max-w-2xl">
        <Input data-testid="hired-equipment-new-name" placeholder="Equipment Name/Description" value={newName} onChange={(e) => setNewName(e.target.value)} className="w-64" />
        <Input data-testid="hired-equipment-new-contractor" placeholder="Contractor/Owner (optional)" value={newContractor} onChange={(e) => setNewContractor(e.target.value)} className="w-56" />
        <Button type="submit" disabled={busy} data-testid="hired-equipment-add-button" className="bg-amber-600 hover:bg-amber-700">
          <Plus className="h-4 w-4 mr-1.5" /> Add
        </Button>
      </form>

      <button
        data-testid="hired-equipment-include-inactive-toggle"
        onClick={() => setIncludeInactive((v) => !v)}
        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
          includeInactive ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
        }`}
      >
        {includeInactive ? "Showing All" : "Showing Active Only"}
      </button>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="hired-equipment-table">
          <TableHeader>
            <TableRow>
              <TableHead>Equipment</TableHead>
              <TableHead>Contractor/Owner</TableHead>
              <TableHead>Usage Count</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                  No hired equipment found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((it) => (
                <TableRow key={it.code} data-testid={`hired-equipment-row-${it.code}`}>
                  <TableCell>
                    {editing === it.code ? (
                      <Input className="h-8" data-testid={`hired-equipment-edit-name-${it.code}`} value={editName} onChange={(e) => setEditName(e.target.value)} />
                    ) : (
                      <span className="font-medium">{it.label}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {editing === it.code ? (
                      <Input className="h-8" data-testid={`hired-equipment-edit-contractor-${it.code}`} value={editContractor} onChange={(e) => setEditContractor(e.target.value)} />
                    ) : (
                      <span className="text-sm text-slate-600">{it.contractor_owner || "—"}</span>
                    )}
                  </TableCell>
                  <TableCell>{it.usage_count}</TableCell>
                  <TableCell>
                    <Badge className={it.active === false ? "bg-slate-400" : "bg-emerald-600"}>{it.active === false ? "Inactive" : "Active"}</Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    {editing === it.code ? (
                      <>
                        <Button size="sm" disabled={busy} data-testid={`hired-equipment-save-${it.code}`} onClick={() => rename(it.code)} className="bg-amber-600 hover:bg-amber-700">
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`hired-equipment-edit-${it.code}`}
                          onClick={() => {
                            setEditing(it.code);
                            setEditName(it.label);
                            setEditContractor(it.contractor_owner || "");
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {it.active !== false && (
                          <Button size="sm" variant="destructive" disabled={busy} data-testid={`hired-equipment-deactivate-${it.code}`} onClick={() => deactivate(it.code)}>
                            <Ban className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
